import { supabase } from '../integrations/client.ts';
import { Database } from '../integrations/types';
import { classifyTopic, TopicClassification } from './topic-classifier.ts';
import {
    SafePromptBuilder,
    sanitizeCoursePrompt,
    sanitizeSectionPrompt,
    sanitizeQuizPrompt,
    escapePromptCharacters,
    wrapInDelimiters,
    createDefensiveSystemMessage
} from '../utils/promptSanitizer.js';

type Course = Database['public']['Tables']['courses']['Row'];
type Chapter = Database['public']['Tables']['chapters']['Row'];
type Section = Database['public']['Tables']['sections']['Row'];
type CourseTemplate = Database['public']['Tables']['course_templates']['Row'];

interface GeneratedCourse {
    chapters: {
        name: string;
        sections: {
            name: string;
            content: string;
        }[];
    }[];
}

// Model selection strategy (Claude 3.5 Haiku for all content)
const MODEL = 'haiku'; // Claude 3.5 Haiku via AWS Bedrock

// Brave Search tool definition
const BRAVE_SEARCH_TOOL = {
    name: "brave_search",
    description: "Search the web for current information, recent developments, version numbers, statistics, or any topic that requires up-to-date knowledge. Use this when generating educational content that references current events, technology versions, market data, or time-sensitive information.",
    input_schema: {
        type: "object",
        properties: {
            query: {
                type: "string",
                description: "The search query. Be specific and include relevant context (e.g., 'Python 3.12 new features 2024' rather than just 'Python features')"
            }
        },
        required: ["query"]
    }
};

/**
 * Normalizes course name for comparison (lowercase, trim, remove extra spaces)
 */
function normalizeName(name: string): string {
    return name.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Determines if a course subject is broad/vague (requires beginner-to-intermediate)
 * or specific (can go beginner-to-expert).
 */
function isVagueSubject(subjectName: string): boolean {
    // Normalize: lowercase, trim
    const normalized = subjectName.toLowerCase().trim();

    // Single or two-word subjects are likely vague if they match broad domains
    const wordCount = normalized.split(/\s+/).length;
    if (wordCount <= 2) {
        // List of broad domains that should cap at intermediate level
        const broadDomains = [
            'physics', 'biology', 'chemistry', 'mathematics', 'math',
            'computer science', 'finance', 'finances', 'economics', 'business',
            'psychology', 'sociology', 'history', 'philosophy', 'engineering',
            'medicine', 'medical', 'law', 'legal', 'music', 'art', 'literature',
            'geography', 'astronomy', 'geology', 'ecology', 'statistics',
            'programming', 'coding', 'data science', 'machine learning',
            'web development', 'app development', 'mobile development',
            'design', 'graphic design', 'marketing', 'sales', 'management',
            'accounting', 'calculus', 'algebra', 'geometry',
            'trigonometry', 'probability', 'language', 'languages'
        ];

        for (const domain of broadDomains) {
            if (normalized.includes(domain)) {
                return true;
            }
        }
    }

    return false;
}

interface Source {
    title: string;
    url: string;
    description?: string;
    accessed_date: string;
}

interface BedrockResponse {
    content: string;
    sources?: Source[];
}

/**
 * Translates technical errors into user-friendly messages
 */
function getUserFriendlyError(error: any): Error {
    // Check if error already has a GENERATION_LIMIT_REACHED name (from database)
    // These errors already have user-friendly messages, so return them as-is
    if (error instanceof Error && error.name === 'GENERATION_LIMIT_REACHED') {
        return error;
    }

    const errorMessage = error?.message || String(error);

    // Extract status code if available
    let statusCode = 500;

    // Check for common status codes in error message
    if (errorMessage.includes('502') || errorMessage.includes('Bad Gateway')) {
        statusCode = 502;
    } else if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        statusCode = 401;
    } else if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
        statusCode = 429;
    } else if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        statusCode = 404;
    } else if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
        statusCode = 403;
    } else if (errorMessage.includes('504') || errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
        statusCode = 504;
    }

    // Generation limit reached (keep original user-friendly message)
    if (errorMessage.includes('GENERATION_LIMIT_REACHED') || errorMessage.includes('monthly limit')) {
        const friendlyError = new Error(errorMessage);
        friendlyError.name = 'GENERATION_LIMIT_REACHED';
        return friendlyError;
    }

    // All other errors get generic message with status code
    const friendlyError = new Error(
        `Error ${statusCode}. Please try again or contact support.`
    );
    friendlyError.name = 'GENERIC_ERROR';
    return friendlyError;
}

/**
 * Calls the Supabase Edge Function to invoke AWS Bedrock with Claude 3.5 Haiku
 * Supports tool_use for Brave Search integration
 */
async function invokeBedrock(
    prompt: string,
    useTools: boolean = false,
    model?: string
): Promise<BedrockResponse> {
    try {
        const body: any = {
            prompt,
            model: model || MODEL  // Use provided model or default
        };

        // Add Brave Search tool if requested
        if (useTools) {
            body.tools = [BRAVE_SEARCH_TOOL];
        }

        const { data, error } = await supabase.functions.invoke('bedrock-invocations', {
            body
        });

        if (error) {
            
            throw getUserFriendlyError(error);
        }

        if (!data) {
            throw new Error('No data returned from Bedrock');
        }

        

        // Extract text content from response
        let content = '';
        if (data?.content && Array.isArray(data.content)) {
            const textBlock = data.content.find((block: any) => block.type === 'text');
            content = textBlock?.text || '';
        }

        if (!content) {
            throw new Error('Invalid response format from Bedrock');
        }

        // Return content and sources (if any)
        return {
            content,
            sources: data.sources
        };
    } catch (err) {
        
        throw getUserFriendlyError(err);
    }
}

/**
 * Parses the course outline response (chapter names only)
 */
function parseCourseOutline(rawText: string): { chapters: { name: string }[] } {
    const chapters: { name: string }[] = [];
    const lines = rawText.split('\n');
    
    for (const line of lines) {
        const match = line.match(/^(?:Chapter\s+\d+:\s*)?(.+)$/i);
        if (match && match[1].trim()) {
            chapters.push({ name: match[1].trim() });
        }
    }
    
    return { chapters };
}

/**
 * Parses section names from a chapter structure response
 */
function parseSectionNames(rawText: string): { sections: { name: string }[] } {
    const sections: { name: string }[] = [];
    const lines = rawText.split('\n');
    
    for (const line of lines) {
        const match = line.match(/^(?:Section\s+\d+:\s*)?(.+)$/i);
        if (match && match[1].trim() && !match[1].includes('Chapter')) {
            sections.push({ name: match[1].trim() });
        }
    }
    
    return { sections };
}

/**
 * Checks if a course template exists, returns it if found
 */
async function findCourseTemplate(courseName: string): Promise<CourseTemplate | null> {
    const normalizedName = normalizeName(courseName);

    const { data, error } = await supabase
        .from('course_templates')
        .select('*')
        .eq('normalized_name', normalizedName)
        .maybeSingle(); // Use maybeSingle() instead of single() to avoid 406 error when no template exists

    if (error || !data) {
        return null;
    }

    return data;
}

/**
 * Creates a course template from generated data
 * Uses atomic upsert to prevent duplicate templates
 */
async function saveCourseTemplate(
    courseName: string,
    templateData: GeneratedCourse,
    isCompleted: boolean = false
): Promise<CourseTemplate> {
    // Try the new atomic upsert function first
    const { data: upsertResult, error: upsertError } = await supabase.rpc('upsert_course_template', {
        p_original_name: courseName,
        p_template_data: templateData as any,
        p_is_completed: isCompleted
    });

    if (!upsertError && upsertResult && upsertResult.length > 0) {
        const result = upsertResult[0];

        // Fetch the full template record
        const { data: template, error: fetchError } = await supabase
            .from('course_templates')
            .select('*')
            .eq('id', result.template_id)
            .single();

        if (fetchError || !template) {
            throw new Error(`Failed to fetch created template: ${fetchError?.message}`);
        }

        return template;
    }

    // Fallback to old insert method if RPC doesn't exist
    const normalizedName = normalizeName(courseName);

    const { data, error } = await supabase
        .from('course_templates')
        .insert({
            normalized_name: normalizedName,
            original_name: courseName,
            template_data: templateData,
            usage_count: 1,
            is_completed: isCompleted
        })
        .select()
        .single();

    if (error || !data) {
        throw new Error(`Failed to save course template: ${error?.message}`);
    }

    return data;
}

/**
 * Increments the usage count for a template (uses new safe increment function)
 */
async function incrementTemplateUsage(templateId: string): Promise<void> {
    // Try new safe increment function first
    const { error: rpcError } = await supabase.rpc('increment_template_usage_safe', {
        p_template_id: templateId
    });

    // If new RPC doesn't exist, try old one
    if (rpcError) {
        const { error } = await supabase.rpc('increment_template_usage', {
            template_id: templateId
        });

        // If old RPC doesn't exist either, fallback to manual increment
        if (error) {
            const { data } = await supabase
                .from('course_templates')
                .select('usage_count')
                .eq('id', templateId)
                .single();

            if (data) {
                await supabase
                    .from('course_templates')
                    .update({ usage_count: (data.usage_count || 0) + 1 })
                    .eq('id', templateId);
            }
        }
    }
}

/**
 * Syncs incomplete courses to a completed template
 * Called when a template is marked as complete
 */
async function syncIncompleteCourseToTemplate(templateId: string): Promise<number> {
    try {
        const { data, error } = await supabase.rpc('sync_incomplete_courses_to_template', {
            p_template_id: templateId
        });

        if (error) {
            return 0;
        }

        return data || 0;
    } catch (error) {
        return 0;
    }
}

/**
 * Gets the user's current generation status (remaining courses, reset date)
 */
export async function getUserGenerationStatus(userId: string): Promise<{
    coursesRemaining: number;
    resetDate: string;
    monthlyLimit: number;
    coursesGenerated: number;
}> {
    try {
        // Call the database function to reset if needed and get status
        const { data, error } = await supabase.rpc('reset_generation_limit_if_needed', {
            user_id: userId
        });

        if (error) {
            
            throw error;
        }

        if (!data || data.length === 0) {
            throw new Error('No status data returned');
        }

        const status = data[0];

        // Also fetch the user's profile for complete info
        const { data: profile } = await supabase
            .from('profiles')
            .select('monthly_course_limit, courses_generated_this_period')
            .eq('id', userId)
            .single();

        return {
            coursesRemaining: status.courses_remaining,
            resetDate: status.reset_date,
            monthlyLimit: profile?.monthly_course_limit || 5,
            coursesGenerated: profile?.courses_generated_this_period || 0
        };
    } catch (error) {
        
        throw error;
    }
}

/**
 * Checks if user can generate a course (does NOT increment counter)
 * Returns error message if limit reached
 */
async function checkGenerationLimit(userId: string, isFromTemplate: boolean): Promise<{
    allowed: boolean;
    coursesRemaining: number;
    errorMessage?: string;
}> {
    try {
        // Only check limit, don't increment yet
        // Note: is_from_template is ignored in the new Stripe-based system
        const { data, error } = await supabase.rpc('check_generation_limit', {
            user_id: userId
        });

        if (error) {

            throw error;
        }

        if (!data || data.length === 0) {
            throw new Error('No limit check data returned');
        }

        const result = data[0];

        // Map new column names to expected format
        const canGenerate = result.can_generate;
        const coursesGenerated = result.courses_generated || 0;
        const maxCourses = result.max_courses || 0;
        const coursesRemaining = Math.max(0, maxCourses - coursesGenerated);

        let errorMessage: string | undefined;
        if (!canGenerate) {
            if (maxCourses === 0) {
                errorMessage = 'Premium subscription required to generate courses. Upgrade to Premium to unlock course generation (5 courses per billing period).';
            } else {
                errorMessage = "You've reached your monthly limit for course generations.";
            }
        }

        return {
            allowed: canGenerate,
            coursesRemaining,
            errorMessage
        };
    } catch (error) {

        throw error;
    }
}

/**
 * Increments the generation counter AFTER successful course creation
 * This ensures users don't lose a generation if course creation fails
 */
async function incrementGenerationCount(userId: string, isFromTemplate: boolean): Promise<void> {
    try {
        // Note: is_from_template is ignored in the new Stripe-based system
        const { error } = await supabase.rpc('increment_generation_count', {
            user_id: userId
        });

        if (error) {

            // Don't throw - we don't want to fail course creation if counter increment fails
            // Log it and continue
        } else {

        }
    } catch (error) {

        // Don't throw - course was created successfully, just log the error
    }
}

/**
 * Creates course structure from template data
 */
async function createCourseFromTemplate(
    userId: string,
    courseName: string,
    template: CourseTemplate
): Promise<Course> {


    // Create the course entry
    const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .insert({
            user_id: userId,
            name: courseName,
            template_id: template.id,
            is_from_template: true,
            is_completed: false
        })
        .select()
        .single();

    if (courseError || !courseData) {
        throw new Error(`Failed to create course: ${courseError?.message}`);
    }

    const templateData: any = template.template_data;
    const createdSections: string[] = [];
    const chapterProjects = templateData.chapter_projects || {};

    // Create chapters and sections
    for (let chapterIndex = 0; chapterIndex < templateData.chapters.length; chapterIndex++) {
        const chapterData = templateData.chapters[chapterIndex];

        const { data: chapter, error: chapterError } = await supabase
            .from('chapters')
            .insert({
                course_id: courseData.id,
                name: chapterData.name,
                order: chapterIndex + 1
            })
            .select()
            .single();

        if (chapterError || !chapter) {
            
            continue;
        }

        // Create chapter project if it exists in template
        if (chapterProjects[chapterIndex]) {
            await supabase
                .from('chapter_projects')
                .insert({
                    chapter_id: chapter.id,
                    instructions: chapterProjects[chapterIndex]
                });
            
        }

        // Create sections
        for (let sectionIndex = 0; sectionIndex < chapterData.sections.length; sectionIndex++) {
            const sectionData = chapterData.sections[sectionIndex];

            const { data: section, error: sectionError } = await supabase
                .from('sections')
                .insert({
                    chapter_id: chapter.id,
                    name: sectionData.name,
                    content: sectionData.content,
                    visual_code: sectionData.visual_code || null, // Preserve visual_code from template
                    order: sectionIndex + 1
                })
                .select()
                .single();

            if (!sectionError && section) {
                createdSections.push(section.id);
            }
        }
    }

    // Initialize user_progress for all sections (only first section unlocked)
    if (createdSections.length > 0) {
        const progressEntries = createdSections.map((sectionId, index) => ({
            user_id: userId,
            course_id: courseData.id,
            section_id: sectionId,
            unlocked: index === 0,
            quiz_completed: false,
            quiz_score: null
        }));

        const { error: progressError } = await supabase
            .from('user_progress')
            .insert(progressEntries);

        if (progressError) {
            
        }
    }

    await incrementTemplateUsage(template.id);

    return courseData;
}

/**
 * Generates a new course curriculum using multi-model approach
 */
async function generateNewCourse(userId: string, subjectName: string, classification?: TopicClassification, userSettings?: { vocabularyLevel?: string; courseLength?: number; isPremium?: boolean }): Promise<Course> {


    // Classify topic if not already provided
    if (!classification) {
        classification = await classifyTopic(subjectName);
    }

    const useTools = classification.requiresRealtime;

    // Step 1: Generate course outline (chapter names only)
    // Use prompt sanitization to prevent injection attacks

    // Detect if subject is vague/broad
    const isVague = isVagueSubject(subjectName);
    const targetLevel = isVague ? "intermediate" : "expert";

    // Get vocabulary level guidance for premium users
    const vocabularyLevel = userSettings?.isPremium && userSettings?.vocabularyLevel !== 'auto'
        ? userSettings.vocabularyLevel
        : null;

    let vocabularyGuidance = "";
    if (vocabularyLevel) {
        switch (vocabularyLevel) {
            case 'simple':
                vocabularyGuidance = `\n\n**VOCABULARY LEVEL: SIMPLE** - Use everyday language suitable for complete beginners. Avoid jargon and technical terms. Explain concepts as if teaching a motivated learner with no prior knowledge.`;
                break;
            case 'intermediate':
                vocabularyGuidance = `\n\n**VOCABULARY LEVEL: INTERMEDIATE** - Use clear technical language with moderate complexity. Assume the learner has some basic background and can handle industry-standard terminology with explanations.`;
                break;
            case 'advanced':
                vocabularyGuidance = `\n\n**VOCABULARY LEVEL: ADVANCED** - Use advanced terminology and assume a strong foundation. The learner should be comfortable with complex concepts and specialized vocabulary in this field.`;
                break;
            case 'phd':
                vocabularyGuidance = `\n\n**VOCABULARY LEVEL: EXPERT/PhD** - Use expert-level academic language with advanced concepts and research terminology. Assume graduate-level understanding and use rigorous academic discourse.`;
                break;
        }
    }

    // Get course length preference for premium users
    const courseLength = userSettings?.isPremium && userSettings?.courseLength && userSettings?.courseLength > 0
        ? userSettings.courseLength
        : null;

    // Add specific guidance for vague subjects
    let vagueGuidance = "";
    if (isVague) {
        vagueGuidance = `

**SPECIAL NOTE: This is a BROAD subject area. Focus on foundational concepts and intermediate-level understanding:**
- Start with fundamental principles and basic concepts
- Build up to practical, intermediate-level applications
- DO NOT attempt to cover advanced or expert-level topics
- Ensure learners have a solid foundation before moving forward
- Each chapter should be accessible to someone with NO prior knowledge`;
    }

    const chapterCountGuidance = courseLength
        ? `\n\n**COURSE LENGTH REQUIREMENT: Generate EXACTLY ${courseLength} chapters.** Do not deviate from this number.`
        : `\n\n**IMPORTANT: Analyze the topic's complexity and breadth BEFORE deciding chapter count.**\n\nList 3-8 chapter titles that progress from complete beginner to ${targetLevel} level. The number of chapters should match the topic's natural scope:\n- Simple tools, frameworks, or focused skills: 3-5 chapters\n- Moderate complexity subjects: 5-6 chapters\n- Complex, broad subjects (sciences, languages, comprehensive domains): 6-8 chapters`;

    const outlinePrompt = new SafePromptBuilder()
        .withSystemMessage(createDefensiveSystemMessage())
        .addInstruction(`You are a professor who's great at teaching almost anything. Create a comprehensive course outline for the subject provided in the user input below.${chapterCountGuidance}${vagueGuidance}${vocabularyGuidance}

**CRITICAL: Each chapter must cover UNIQUE, NON-REDUNDANT content. It's better to have 4 high-quality, distinct chapters than 8 chapters with repeated concepts. Avoid filler content.**

ONLY provide the chapter names, nothing else.

Format:
Chapter 1: [Name]
Chapter 2: [Name]
Chapter 3: [Name]
...
${courseLength ? `Chapter ${courseLength}: [Name]` : 'Chapter 3-8: [Name]'}`)
        .addUserInput(subjectName, 'subject_name')
        .build();

    
    const outlineResponse = await invokeBedrock(outlinePrompt, false);
    const outline = parseCourseOutline(outlineResponse.content);
    
    

    // Step 2: Generate section names for ALL chapters in parallel

    const sectionNamePromises = outline.chapters.map((chapter, chapterIndex) => {
        // Use prompt sanitization to prevent injection attacks
        const sectionPrompt = new SafePromptBuilder()
            .withSystemMessage(createDefensiveSystemMessage())
            .addInstruction(`List exactly 7 section names for the chapter specified in the metadata below. The sections should cover key topics in a logical progression.

ONLY provide section names, nothing else.

Format:
Section 1: [Name]
Section 2: [Name]
Section 3: [Name]
Section 4: [Name]
Section 5: [Name]
Section 6: [Name]
Section 7: [Name]`)
            .addMetadata({
                course_subject: subjectName,
                chapter_name: chapter.name
            })
            .build();


        return invokeBedrock(sectionPrompt, false);
    });

    const sectionResponses = await Promise.all(sectionNamePromises);
    const allChapterSections = sectionResponses.map(response => parseSectionNames(response.content));

    // Step 3: Generate content for ONLY the first section of the FIRST chapter
    
    let firstSectionContent = { content: '', sources: [] };

    const firstChapter = outline.chapters[0];
    const firstSectionName = allChapterSections[0].sections[0]?.name;

    if (firstSectionName) {
        // Use prompt sanitization to prevent injection attacks
        const contentPrompt = new SafePromptBuilder()
            .withSystemMessage(createDefensiveSystemMessage())
            .addInstruction(`You are teaching a course. The course details are provided in the metadata below.

Write a comprehensive, engaging educational explanation that:
- Starts with an engaging introduction showing real-world relevance
- Provides necessary background context the learner needs
- Deep dives into the main ideas with clear explanations and examples
- Shows real-world examples, use cases, and practical applications
- Ends with a recap of key takeaways${vocabularyGuidance}

Target: 5-7 paragraphs, approximately 600 words total. Make it clear, educational, and engaging. Write naturally without using structural labels like "Hook", "Foundation", "Core Concept", etc. Just present the information in a flowing, coherent manner.

You may use **KaTeX** for mathematical equations:
- Inline: $E=mc^2$
- Display: $$\\int_0^1 x dx$$

Use **Markdown** for all text formatting, lists, tables, code blocks, etc.

If you need current information (version numbers, recent developments, statistics, dates), use the brave_search tool to get up-to-date information. DO NOT cite sources in the content - they will be added automatically as footnotes.`)
            .addMetadata({
                course_subject: subjectName,
                chapter_name: firstChapter.name,
                section_name: firstSectionName,
                chapter_number: '1',
                section_number: '1'
            })
            .build();


        firstSectionContent = await invokeBedrock(contentPrompt, useTools); // Conditionally enable tools based on classification
    }

    // Step 4: Assemble the full course structure
    const fullCourse: GeneratedCourse = { chapters: [] };

    for (let chapterIndex = 0; chapterIndex < outline.chapters.length; chapterIndex++) {
        const chapterName = outline.chapters[chapterIndex].name;
        const sectionNames = allChapterSections[chapterIndex];
        const sections: { name: string; content: string }[] = [];

        for (let sectionIndex = 0; sectionIndex < sectionNames.sections.length; sectionIndex++) {
            const sectionName = sectionNames.sections[sectionIndex].name;
            // Only add content to the first section of the first chapter
            const content = (chapterIndex === 0 && sectionIndex === 0) ? firstSectionContent.content : '';
            sections.push({ name: sectionName, content });
        }

        fullCourse.chapters.push({
            name: chapterName,
            sections
        });
    }

    // Step 5: Generate chapter projects for all chapters (ONLY for skill-based courses)
    
    const chapterProjects: { [chapterIndex: number]: string } = {};

    // Only generate projects for skill-based courses (programming, design, trading, etc.)
    // Skip for knowledge-based courses (history, news, economics theory, etc.)
    if (classification.isSkillBased) {
        

        // Generate projects for all chapters in parallel
        const projectPromises = fullCourse.chapters.map(async (chapter, chapterIndex) => {
            const sectionSummaries = chapter.sections
                .map((section, index) => {
                    const contentPreview = section.content?.substring(0, 300) || '[Content will be generated]';
                    return `Section ${index + 1}: ${section.name}\n${contentPreview}...`;
                })
                .join('\n\n');

            // Use prompt sanitization to prevent injection attacks

            // Determine if this is a coding/programming subject
            const isCodingSubject = /programming|coding|software|web development|app development|python|javascript|java|c\+\+|react|angular|vue|frontend|backend|full stack|algorithm|data structures/i.test(subjectName);

            const projectType = isCodingSubject ? 'hands-on coding project' : 'hands-on practice project';
            const projectInstruction = isCodingSubject
                ? 'Create a practical, real-world coding project'
                : 'Create a practical, hands-on project (NO CODING - use pen/paper, calculator, or conceptual exercises)';

            const criticalInstructions = isCodingSubject
                ? '- This IS a coding/programming course - create a coding project with code examples'
                : `- This is NOT a coding course - DO NOT require any programming/coding
- Use pen and paper, calculator, spreadsheet, or mental exercises
- For STEM subjects: provide practice problems, calculations, derivations, or experiments
- For non-STEM subjects: provide research tasks, analysis exercises, or creative projects`;

            const projectPrompt = new SafePromptBuilder()
                .withSystemMessage(createDefensiveSystemMessage())
                .addInstruction(`You are creating a ${projectType} for a course. The course details and section summaries are provided in the metadata below.

${projectInstruction} that:
- Applies concepts from all sections in this chapter
- Can be completed in 30-60 minutes
- Includes clear, step-by-step instructions
- Is appropriate for learners who just completed this chapter
- Provides expected outcomes/results to verify completion
- Includes 2-3 optional bonus challenges for advanced learners

**CRITICAL INSTRUCTIONS BASED ON SUBJECT TYPE:**
${criticalInstructions}

Format your response as markdown with these sections:
# 📁 Project Title (make it engaging and relevant)

## Objective
[1-2 sentences describing what the learner will ${isCodingSubject ? 'build/create' : 'work on/complete'}]

## Prerequisites
[List any tools, setup, or prior knowledge needed - ${isCodingSubject ? 'code editor, libraries, etc.' : 'pen/paper, calculator, or other non-coding materials'}]

## Instructions
[Numbered steps with clear, actionable instructions. Be specific but not overly prescriptive - learners should have some creative freedom]

## Expected Results
[Describe what success looks like - what should the project ${isCodingSubject ? 'produce/demonstrate' : 'show/achieve'} when complete?]

## Bonus Challenges
[2-3 optional extensions to deepen learning]

## Reflection Questions
[2-3 questions to help learners reflect on what they learned]

IMPORTANT: Make this practical and achievable. The project should reinforce learning, not overwhelm.`)
                .addMetadata({
                    course_subject: subjectName,
                    chapter_name: chapter.name,
                    chapter_number: String(chapterIndex + 1)
                })
                .addUserInput(sectionSummaries, 'section_summaries')
                .build();


            const projectResponse = await invokeBedrock(projectPrompt, false);
            chapterProjects[chapterIndex] = projectResponse.content;
        });

        await Promise.all(projectPromises);
        
    } else {

    }

    // Step 6: Create user's course (premium users do NOT create templates)

    const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .insert({
            user_id: userId,
            name: subjectName,
            template_id: null, // Premium courses are NOT linked to templates
            is_from_template: false,
            is_completed: false,
            requires_realtime: classification.requiresRealtime,
            is_skill_based: classification.isSkillBased,
            classification_reason: classification.reason,
            classification_confidence: classification.confidence
        })
        .select()
        .single();

    if (courseError || !courseData) {
        throw new Error(`Failed to create course: ${courseError?.message}`);
    }

    // Step 8: Create chapters, sections, and chapter projects in user's course
    const createdSections: string[] = [];

    for (let chapterIndex = 0; chapterIndex < fullCourse.chapters.length; chapterIndex++) {
        const chapterData = fullCourse.chapters[chapterIndex];

        const { data: chapter, error: chapterError } = await supabase
            .from('chapters')
            .insert({
                course_id: courseData.id,
                name: chapterData.name,
                order: chapterIndex + 1
            })
            .select()
            .single();

        if (chapterError || !chapter) {
            
            continue;
        }

        // Create chapter project if it exists in template
        if (chapterProjects[chapterIndex]) {
            await supabase
                .from('chapter_projects')
                .insert({
                    chapter_id: chapter.id,
                    instructions: chapterProjects[chapterIndex]
                });
            
        }

        for (let sectionIndex = 0; sectionIndex < chapterData.sections.length; sectionIndex++) {
            const sectionData = chapterData.sections[sectionIndex];

            const { data: section, error: sectionError } = await supabase
                .from('sections')
                .insert({
                    chapter_id: chapter.id,
                    name: sectionData.name,
                    content: sectionData.content,
                    order: sectionIndex + 1
                })
                .select()
                .single();

            if (!sectionError && section) {
                createdSections.push(section.id);
            }
        }
    }

    // Step 9: Initialize user_progress for all sections (only first section unlocked)
    if (createdSections.length > 0) {
        const progressEntries = createdSections.map((sectionId, index) => ({
            user_id: userId,
            course_id: courseData.id,
            section_id: sectionId,
            unlocked: index === 0,
            quiz_completed: false,
            quiz_score: null
        }));

        const { error: progressError } = await supabase
            .from('user_progress')
            .insert(progressEntries);

        if (progressError) {
            
        }
    }

    return courseData;
}

/**
 * FAST Initial Course Generation - Only generates Chapter 1
 * Allows immediate navigation to Learn page while background completes
 */
export async function generateCourseInitial(userId: string, subjectName: string, classification?: TopicClassification, userSettings?: { vocabularyLevel?: string; courseLength?: number; isPremium?: boolean }): Promise<Course> {

    // Check if template exists (to determine if this counts toward limit)
    const existingTemplate = await findCourseTemplate(subjectName);
    const isFromTemplate = !!existingTemplate;

    // CHECK GENERATION LIMIT (template reuse doesn't count)
    const limitCheck = await checkGenerationLimit(userId, isFromTemplate);

    if (!limitCheck.allowed) {
        // Throw error with user-friendly message
        const error = new Error(limitCheck.errorMessage || 'Course generation limit reached');
        error.name = 'GENERATION_LIMIT_REACHED';
        throw error;
    }

    // Classify topic if not already provided
    if (!classification) {
        classification = await classifyTopic(subjectName);
    }

    const useTools = classification.requiresRealtime;

    // Detect if subject is vague/broad
    const isVague = isVagueSubject(subjectName);
    const targetLevel = isVague ? "intermediate" : "expert";

    // Get vocabulary level guidance for premium users
    const vocabularyLevel = userSettings?.isPremium && userSettings?.vocabularyLevel !== 'auto'
        ? userSettings.vocabularyLevel
        : null;

    let vocabularyGuidance = "";
    if (vocabularyLevel) {
        switch (vocabularyLevel) {
            case 'simple':
                vocabularyGuidance = `\n\n**VOCABULARY LEVEL: SIMPLE** - Use everyday language suitable for complete beginners. Avoid jargon and technical terms. Explain concepts as if teaching a motivated learner with no prior knowledge.`;
                break;
            case 'intermediate':
                vocabularyGuidance = `\n\n**VOCABULARY LEVEL: INTERMEDIATE** - Use clear technical language with moderate complexity. Assume the learner has some basic background and can handle industry-standard terminology with explanations.`;
                break;
            case 'advanced':
                vocabularyGuidance = `\n\n**VOCABULARY LEVEL: ADVANCED** - Use advanced terminology and assume a strong foundation. The learner should be comfortable with complex concepts and specialized vocabulary in this field.`;
                break;
            case 'phd':
                vocabularyGuidance = `\n\n**VOCABULARY LEVEL: EXPERT/PhD** - Use expert-level academic language with advanced concepts and research terminology. Assume graduate-level understanding and use rigorous academic discourse.`;
                break;
        }
    }

    // Get course length preference for premium users
    const courseLength = userSettings?.isPremium && userSettings?.courseLength && userSettings?.courseLength > 0
        ? userSettings.courseLength
        : null;

    // Add specific guidance for vague subjects
    let vagueGuidance = "";
    if (isVague) {
        vagueGuidance = `

**SPECIAL NOTE: This is a BROAD subject area. Focus on foundational concepts and intermediate-level understanding:**
- Start with fundamental principles and basic concepts
- Build up to practical, intermediate-level applications
- DO NOT attempt to cover advanced or expert-level topics
- Ensure learners have a solid foundation before moving forward
- Each chapter should be accessible to someone with NO prior knowledge`;
    }

    const chapterCountGuidance = courseLength
        ? `\n\n**COURSE LENGTH REQUIREMENT: Generate EXACTLY ${courseLength} chapters.** Do not deviate from this number.`
        : `\n\n**IMPORTANT: Analyze the topic's complexity and breadth BEFORE deciding chapter count.**\n\nList 3-8 chapter titles that progress from complete beginner to ${targetLevel} level. The number of chapters should match the topic's natural scope:\n- Simple tools, frameworks, or focused skills: 3-5 chapters\n- Moderate complexity subjects: 5-6 chapters\n- Complex, broad subjects (sciences, languages, comprehensive domains): 6-8 chapters`;

    // Step 1: Generate course outline (chapter names only)
    const outlinePrompt = new SafePromptBuilder()
        .withSystemMessage(createDefensiveSystemMessage())
        .addInstruction(`You are a professor who's great at teaching almost anything. Create a comprehensive course outline for the subject provided in the user input below.${chapterCountGuidance}${vagueGuidance}${vocabularyGuidance}

**CRITICAL: Each chapter must cover UNIQUE, NON-REDUNDANT content. It's better to have 4 high-quality, distinct chapters than 8 chapters with repeated concepts. Avoid filler content.**

ONLY provide the chapter names, nothing else.

Format:
Chapter 1: [Name]
Chapter 2: [Name]
Chapter 3: [Name]
...
${courseLength ? `Chapter ${courseLength}: [Name]` : 'Chapter 3-8: [Name]'}`)
        .addUserInput(subjectName, 'subject_name')
        .build();


    const outlineResponse = await invokeBedrock(outlinePrompt, false);
    const outline = parseCourseOutline(outlineResponse.content);



    // Step 2: Generate section names for FIRST CHAPTER ONLY
    const firstChapter = outline.chapters[0];
    const sectionPrompt = new SafePromptBuilder()
        .withSystemMessage(createDefensiveSystemMessage())
        .addInstruction(`List exactly 7 section names for the chapter specified in the metadata below. The sections should cover key topics in a logical progression.

ONLY provide section names, nothing else.

Format:
Section 1: [Name]
Section 2: [Name]
Section 3: [Name]
Section 4: [Name]
Section 5: [Name]
Section 6: [Name]
Section 7: [Name]`)
        .addMetadata({
            course_subject: subjectName,
            chapter_name: firstChapter.name
        })
        .build();

    const sectionResponse = await invokeBedrock(sectionPrompt, false);
    const firstChapterSections = parseSectionNames(sectionResponse.content);

    // Step 3: Generate content for first section only
    const firstSectionName = firstChapterSections.sections[0]?.name;
    let firstSectionContent = { content: '', sources: [] };

    if (firstSectionName) {
        const contentPrompt = new SafePromptBuilder()
            .withSystemMessage(createDefensiveSystemMessage())
            .addInstruction(`You are teaching a course. The course details are provided in the metadata below.

Write a comprehensive, engaging educational explanation that:
- Starts with an engaging introduction showing real-world relevance
- Provides necessary background context the learner needs
- Deep dives into the main ideas with clear explanations and examples
- Shows real-world examples, use cases, and practical applications
- Ends with a recap of key takeaways${vocabularyGuidance}

Target: 5-7 paragraphs, approximately 600 words total. Make it clear, educational, and engaging. Write naturally without using structural labels like "Hook", "Foundation", "Core Concept", etc. Just present the information in a flowing, coherent manner.

You may use **KaTeX** for mathematical equations:
- Inline: $E=mc^2$
- Display: $$\\int_0^1 x dx$$

Use **Markdown** for all text formatting, lists, tables, code blocks, etc.

If you need current information (version numbers, recent developments, statistics, dates), use the brave_search tool to get up-to-date information. DO NOT cite sources in the content - they will be added automatically as footnotes.`)
            .addMetadata({
                course_subject: subjectName,
                chapter_name: firstChapter.name,
                section_name: firstSectionName,
                chapter_number: '1',
                section_number: '1'
            })
            .build();

        firstSectionContent = await invokeBedrock(contentPrompt, useTools);
    }

    // Step 4: Save as template with ONLY first chapter
    const partialTemplate = {
        chapters: [{
            name: firstChapter.name,
            sections: firstChapterSections.sections.map((section, index) => ({
                name: section.name,
                content: index === 0 ? firstSectionContent.content : '' // Only first section has content
            }))
        }],
        fullOutline: outline.chapters // Store full outline for background generation
    };

    // Step 5: Create user's course with generation_status = 'completing' (premium users do NOT create templates)
    const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .insert({
            user_id: userId,
            name: subjectName,
            template_id: null, // Premium courses are NOT linked to templates
            is_from_template: false,
            is_completed: false,
            requires_realtime: classification.requiresRealtime,
            is_skill_based: classification.isSkillBased,
            classification_reason: classification.reason,
            classification_confidence: classification.confidence,
            generation_status: 'completing' // Mark as incomplete
        })
        .select()
        .single();

    if (courseError || !courseData) {
        throw new Error(`Failed to create course: ${courseError?.message}`);
    }

    // Step 6: Create ONLY first chapter structure
    const createdSections: string[] = [];

    const { data: chapter, error: chapterError } = await supabase
        .from('chapters')
        .insert({
            course_id: courseData.id,
            name: firstChapter.name,
            order: 1
        })
        .select()
        .single();

    if (chapterError || !chapter) {
        throw new Error(`Failed to create first chapter: ${chapterError?.message}`);
    }

    // Create sections for first chapter
    for (let sectionIndex = 0; sectionIndex < firstChapterSections.sections.length; sectionIndex++) {
        const sectionData = firstChapterSections.sections[sectionIndex];

        const { data: section, error: sectionError } = await supabase
            .from('sections')
            .insert({
                chapter_id: chapter.id,
                name: sectionData.name,
                content: sectionIndex === 0 ? firstSectionContent.content : '', // Only first has content
                sources: sectionIndex === 0 ? firstSectionContent.sources : undefined, // Include sources if available
                order: sectionIndex + 1
            })
            .select()
            .single();

        if (!sectionError && section) {
            createdSections.push(section.id);
        }
    }

    // Step 7: Initialize user_progress for first chapter sections only
    if (createdSections.length > 0) {
        const progressEntries = createdSections.map((sectionId, index) => ({
            user_id: userId,
            course_id: courseData.id,
            section_id: sectionId,
            unlocked: index === 0, // Only first section unlocked
            quiz_completed: false,
            quiz_score: null
        }));

        await supabase
            .from('user_progress')
            .insert(progressEntries);
    }

    // Increment generation count AFTER successful creation
    await incrementGenerationCount(userId, isFromTemplate);

    // Return course data with outline for background generation
    return {
        ...courseData,
        fullOutline: outline.chapters // Include outline for background generation
    };
}

/**
 * Background generation - completes remaining chapters/sections
 * Runs asynchronously after user navigates to Learn page
 */
export async function continueGenerationInBackground(
    courseId: string,
    userId: string,
    subjectName: string,
    classification: TopicClassification,
    userSettings?: { vocabularyLevel?: string; courseLength?: number; isPremium?: boolean },
    fullOutline?: any[] // Pass the outline directly instead of fetching from template
): Promise<void> {
    try {
        // Premium courses do NOT use templates - outline is passed directly or stored in course metadata
        if (!fullOutline) {
            throw new Error('Course outline not provided for background generation');
        }

        if (fullOutline.length <= 1) {
            // Only 1 chapter, mark as complete
            await supabase
                .from('courses')
                .update({ generation_status: 'complete' })
                .eq('id', courseId);
            return;
        }

        // Step 1: Generate section names for remaining chapters (parallel)
        const remainingChapters = fullOutline.slice(1); // Skip first chapter
        const sectionNamePromises = remainingChapters.map((chapter: any) => {
            const sectionPrompt = new SafePromptBuilder()
                .withSystemMessage(createDefensiveSystemMessage())
                .addInstruction(`List exactly 7 section names for the chapter specified in the metadata below. The sections should cover key topics in a logical progression.

ONLY provide section names, nothing else.

Format:
Section 1: [Name]
Section 2: [Name]
Section 3: [Name]
Section 4: [Name]
Section 5: [Name]
Section 6: [Name]
Section 7: [Name]`)
                .addMetadata({
                    course_subject: subjectName,
                    chapter_name: chapter.name
                })
                .build();

            return invokeBedrock(sectionPrompt, false);
        });

        const sectionResponses = await Promise.all(sectionNamePromises);
        const allChapterSections = sectionResponses.map(response => parseSectionNames(response.content));

        // Step 2: Generate chapter projects for ALL chapters if skill-based
        const chapterProjects: { [chapterIndex: number]: string } = {};

        if (classification.isSkillBased) {
            // Get first chapter sections from database
            const { data: firstChapter } = await supabase
                .from('chapters')
                .select('*, sections(*)')
                .eq('course_id', courseId)
                .eq('order', 1)
                .single();

            const allChapters = [fullOutline[0], ...remainingChapters];
            const projectPromises = allChapters.map(async (chapter: any, chapterIndex: number) => {
                const sections = chapterIndex === 0
                    ? firstChapter?.sections || []
                    : allChapterSections[chapterIndex - 1].sections;

                const sectionSummaries = sections
                    .map((section: any, index: number) => {
                        const contentPreview = section.content?.substring(0, 300) || '[Content will be generated]';
                        return `Section ${index + 1}: ${section.name}\n${contentPreview}...`;
                    })
                    .join('\n\n');

                const isCodingSubject = /programming|coding|software|web development|app development|python|javascript|java|c\+\+|react|angular|vue|frontend|backend|full stack|algorithm|data structures/i.test(subjectName);

                const projectType = isCodingSubject ? 'hands-on coding project' : 'hands-on practice project';
                const projectInstruction = isCodingSubject
                    ? 'Create a practical, real-world coding project'
                    : 'Create a practical, hands-on project (NO CODING - use pen/paper, calculator, or conceptual exercises)';

                const criticalInstructions = isCodingSubject
                    ? '- This IS a coding/programming course - create a coding project with code examples'
                    : `- This is NOT a coding course - DO NOT require any programming/coding
- Use pen and paper, calculator, spreadsheet, or mental exercises
- For STEM subjects: provide practice problems, calculations, derivations, or experiments
- For non-STEM subjects: provide research tasks, analysis exercises, or creative projects`;

                const projectPrompt = new SafePromptBuilder()
                    .withSystemMessage(createDefensiveSystemMessage())
                    .addInstruction(`You are creating a ${projectType} for a course. The course details and section summaries are provided in the metadata below.

${projectInstruction} that:
- Applies concepts from all sections in this chapter
- Can be completed in 30-60 minutes
- Includes clear, step-by-step instructions
- Is appropriate for learners who just completed this chapter
- Provides expected outcomes/results to verify completion
- Includes 2-3 optional bonus challenges for advanced learners

**CRITICAL INSTRUCTIONS BASED ON SUBJECT TYPE:**
${criticalInstructions}

Format your response as markdown with these sections:
# 📁 Project Title (make it engaging and relevant)

## Objective
[1-2 sentences describing what the learner will ${isCodingSubject ? 'build/create' : 'work on/complete'}]

## Prerequisites
[List any tools, setup, or prior knowledge needed - ${isCodingSubject ? 'code editor, libraries, etc.' : 'pen/paper, calculator, or other non-coding materials'}]

## Instructions
[Numbered steps with clear, actionable instructions. Be specific but not overly prescriptive - learners should have some creative freedom]

## Expected Results
[Describe what success looks like - what should the project ${isCodingSubject ? 'produce/demonstrate' : 'show/achieve'} when complete?]

## Bonus Challenges
[2-3 optional extensions to deepen learning]

## Reflection Questions
[2-3 questions to help learners reflect on what they learned]

IMPORTANT: Make this practical and achievable. The project should reinforce learning, not overwhelm.`)
                    .addMetadata({
                        course_subject: subjectName,
                        chapter_name: chapter.name,
                        chapter_number: String(chapterIndex + 1)
                    })
                    .addUserInput(sectionSummaries, 'section_summaries')
                    .build();

                const projectResponse = await invokeBedrock(projectPrompt, false);
                chapterProjects[chapterIndex] = projectResponse.content;
            });

            await Promise.all(projectPromises);
        }

        // Step 3: Create project for first chapter (if skill-based)
        if (classification.isSkillBased && chapterProjects[0]) {
            // Get first chapter ID
            const { data: firstChapter } = await supabase
                .from('chapters')
                .select('id')
                .eq('course_id', courseId)
                .eq('order', 1)
                .single();

            if (firstChapter) {
                await supabase
                    .from('chapter_projects')
                    .insert({
                        chapter_id: firstChapter.id,
                        instructions: chapterProjects[0]
                    });
            }
        }

        // Step 4b: Create remaining chapters and sections in user's course
        for (let chapterIndex = 1; chapterIndex < fullOutline.length; chapterIndex++) {
            const chapterData = fullOutline[chapterIndex];

            // Validate data before insertion
            if (!courseId || !chapterData.name) {
                continue;
            }

            const { data: chapter, error: chapterError } = await supabase
                .from('chapters')
                .insert({
                    course_id: courseId,
                    name: chapterData.name,
                    order: chapterIndex + 1
                })
                .select()
                .single();

            if (chapterError || !chapter) {
                continue;
            }

            // Create chapter project if exists
            if (chapterProjects[chapterIndex]) {
                await supabase
                    .from('chapter_projects')
                    .insert({
                        chapter_id: chapter.id,
                        instructions: chapterProjects[chapterIndex]
                    });
            }

            // Create sections
            const sections = allChapterSections[chapterIndex - 1].sections;
            const createdSections: string[] = [];

            for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
                const sectionData = sections[sectionIndex];

                const { data: section, error: sectionError } = await supabase
                    .from('sections')
                    .insert({
                        chapter_id: chapter.id,
                        name: sectionData.name,
                        content: '', // Empty content, generated on-demand
                        order: sectionIndex + 1
                    })
                    .select()
                    .single();

                if (!sectionError && section) {
                    createdSections.push(section.id);
                }
            }

            // Initialize user_progress for these sections (all locked)
            if (createdSections.length > 0) {
                const progressEntries = createdSections.map(sectionId => ({
                    user_id: userId,
                    course_id: courseId,
                    section_id: sectionId,
                    unlocked: false, // All locked
                    quiz_completed: false,
                    quiz_score: null
                }));

                await supabase
                    .from('user_progress')
                    .insert(progressEntries);
            }
        }

        // Step 5: Mark course as complete
        await supabase
            .from('courses')
            .update({ generation_status: 'complete' })
            .eq('id', courseId);


    } catch (error) {

        // Mark course as failed or keep as completing
        await supabase
            .from('courses')
            .update({ generation_status: 'complete' }) // Still mark complete even if some parts failed
            .eq('id', courseId);
    }
}

/**
 * Main function: Generates or loads a course
 */
export async function generateCourse(userId: string, subjectName: string, classification?: TopicClassification, userSettings?: { vocabularyLevel?: string; courseLength?: number; isPremium?: boolean }): Promise<Course> {
    try {
        // Classify topic first if not provided
        if (!classification) {
            classification = await classifyTopic(subjectName);
        }

        // Check if template exists (to determine if this counts toward limit)
        const existingTemplate = await findCourseTemplate(subjectName);
        const isFromTemplate = !!existingTemplate;

        // CHECK GENERATION LIMIT (template reuse doesn't count)
        const limitCheck = await checkGenerationLimit(userId, isFromTemplate);

        if (!limitCheck.allowed) {
            // Throw error with user-friendly message
            const error = new Error(limitCheck.errorMessage || 'Course generation limit reached');
            error.name = 'GENERATION_LIMIT_REACHED';
            throw error;
        }



        if (existingTemplate) {
            const course = await createCourseFromTemplate(userId, subjectName, existingTemplate);

            // Update the course with classification data
            await supabase
                .from('courses')
                .update({
                    requires_realtime: classification.requiresRealtime,
                    is_skill_based: classification.isSkillBased,
                    classification_reason: classification.reason,
                    classification_confidence: classification.confidence
                })
                .eq('id', course.id);

            // Increment counter AFTER successful creation (templates don't count)
            await incrementGenerationCount(userId, isFromTemplate);

            return course;
        }

        const newCourse = await generateNewCourse(userId, subjectName, classification, userSettings);

        // Increment counter AFTER successful creation (only for new courses)
        await incrementGenerationCount(userId, isFromTemplate);

        return newCourse;
    } catch (error) {
        
        // If error is already a friendly error (has a name like CONNECTION_ERROR), pass it through
        // Otherwise, translate it to a friendly error
        if (error instanceof Error && error.name && error.name.includes('ERROR')) {
            throw error;
        }
        throw getUserFriendlyError(error);
    }
}

/**
 * Generates content for a specific section (called when user unlocks it)
 */
export async function generateSectionContent(
    sectionId: string,
    courseName: string,
    chapterName: string,
    sectionName: string,
    chapterOrder: number
): Promise<string> {
    try {
        

        // Check if content already exists and get course classification
        const { data: existingSection } = await supabase
            .from('sections')
            .select('content, sources, chapter_id, chapters!inner(course_id, courses!inner(requires_realtime, is_from_template))')
            .eq('id', sectionId)
            .single();

        if (existingSection?.content && existingSection.content.trim() !== '') {

            return existingSection.content;
        }

        // SECURITY CHECK: Verify the section is unlocked for the current user
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            throw new Error('User must be authenticated to generate content');
        }

        // SECURITY CHECK: Prevent content generation for template courses
        // Template courses should have all content pre-generated
        const isFromTemplate = existingSection?.chapters?.courses?.is_from_template || false;
        if (isFromTemplate) {
            throw new Error('Cannot generate content for template courses. All content should already exist.');
        }

        // SECURITY CHECK: Only premium users can generate custom course content
        const { data: userProfile } = await supabase
            .from('profiles')
            .select('subscription_type')
            .eq('id', user.id)
            .single();

        if (userProfile?.subscription_type !== 'premium') {
            throw new Error('Only premium users can generate course content.');
        }

        const courseId = existingSection?.chapters?.course_id;
        if (!courseId) {
            throw new Error('Could not determine course ID for section');
        }

        // Check if this section is unlocked in user_progress
        const { data: progressData, error: progressError } = await supabase
            .from('user_progress')
            .select('unlocked')
            .eq('user_id', user.id)
            .eq('section_id', sectionId)
            .eq('course_id', courseId)
            .single();

        if (progressError || !progressData?.unlocked) {
            
            throw new Error('This section is locked. Complete the previous section to unlock it.');
        }

        

        // Determine if we should use tools based on course classification
        const requiresRealtime = existingSection?.chapters?.courses?.requires_realtime || false;
        

        // Generate new content with educational structure
        // Use prompt sanitization to prevent injection attacks
        const contentPrompt = new SafePromptBuilder()
            .withSystemMessage(createDefensiveSystemMessage())
            .addInstruction(`You are teaching a course. The course details are provided in the metadata below.

Write a comprehensive, engaging educational explanation that:
- Starts with an engaging introduction showing real-world relevance
- Provides necessary background context the learner needs
- Deep dives into the main ideas with clear explanations and examples
- Shows real-world examples, use cases, and practical applications
- Ends with a recap of key takeaways${vocabularyGuidance}

Target: 5-7 paragraphs, approximately 600 words total. Make it clear, educational, and engaging. Write naturally without using structural labels like "Hook", "Foundation", "Core Concept", etc. Just present the information in a flowing, coherent manner.

You may use **KaTeX** for mathematical equations:
- Inline: $E=mc^2$
- Display: $$\\int_0^1 x dx$$

Use **Markdown** for all text formatting, lists, tables, code blocks, etc.

If you need current information (version numbers, recent developments, statistics, dates), use the brave_search tool to get up-to-date information. DO NOT cite sources in the content - they will be added automatically as footnotes.`)
            .addMetadata({
                course_name: courseName,
                chapter_name: chapterName,
                section_name: sectionName,
                chapter_number: String(chapterOrder)
            })
            .build();



        const response = await invokeBedrock(contentPrompt, requiresRealtime); // Conditionally enable tools

        // Save the generated content and sources
        const updateData: any = {
            content: response.content
        };

        if (response.sources && response.sources.length > 0) {
            updateData.sources = response.sources;
            
        }

        const { error: updateError } = await supabase
            .from('sections')
            .update(updateData)
            .eq('id', sectionId);

        if (updateError) {
            
            throw new Error(`Failed to save section content: ${updateError.message}`);
        }

        
        return response.content;
    } catch (error) {
        
        // Pass through friendly errors, translate others
        if (error instanceof Error && error.name && error.name.includes('ERROR')) {
            throw error;
        }
        throw getUserFriendlyError(error);
    }
}

/**
 * Generates a ThreeJS visualization for a specific section based on its content
 * Checks database first - only generates if visual doesn't exist yet
 */
export async function generateSectionVisual(
    sectionId: string,
    sectionContent: string
): Promise<string | null> {
    try {

        // ALWAYS check if visual already exists first (critical for performance)
        const { data: existingSection } = await supabase
            .from('sections')
            .select('visual_code')
            .eq('id', sectionId)
            .single();

        // If visual exists, return it immediately (NO regeneration)
        if (existingSection?.visual_code) {
            return existingSection.visual_code;
        }

        // Only generate if visual doesn't exist yet

        // Create focused visual generation prompt
        // NOTE: Qwen models require plain text prompts (not SafePromptBuilder's XML structure)
        const visualPrompt = `You are an expert at creating educational 2D visualizations using Three.js.

Your task is to create a 2D THREE.JS visualization that DIRECTLY demonstrates the specific concepts explained in the section content below.

**CRITICAL REQUIREMENTS:**

1. **2D FLAT DESIGN ONLY**:
   - Use orthographic camera positioned at (0, 0, 5) looking at origin
   - Create flat 2D shapes using PlaneGeometry, CircleGeometry, or RingGeometry
   - NO 3D objects (no BoxGeometry, SphereGeometry, or 3D meshes)
   - Think of it as a 2D diagram/infographic, not a 3D scene
   - Position all objects on the same Z plane (z = 0)

2. **MANDATORY LABELING**:
   - EVERY shape/object MUST have a visible text label
   - Use HTML div overlays positioned absolutely to label objects
   - Labels should clearly identify what each element represents
   - Example: If showing a cell, label "Nucleus", "Mitochondria", "Cell Membrane"
   - Use arrows or lines to connect labels to their objects if needed

3. **Content-Specific Visualization**: The visual MUST demonstrate the EXACT concepts/topics from the section
   - For a section about "photosynthesis", show a labeled flat diagram of chloroplast with "Thylakoid", "Stroma", "Light", "CO2"
   - For a section about "supply and demand", show labeled curves with "Supply Curve", "Demand Curve", "Equilibrium Point"
   - For a section about "recursion", show a labeled tree diagram with "Base Case", "Recursive Call 1", "Recursive Call 2"
   - DO NOT create generic unlabeled shapes

4. **Visual Type**:
   - **Labeled diagrams with gentle motion** (preferred):
     * Flat 2D shapes with text labels
     * Gentle pulsing, floating, or color transitions for emphasis
     * Clean lines and clear spatial relationships

5. **Code Requirements:**
   - MAX 2200 TOKENS total
   - Use OrthographicCamera for true 2D rendering
   - Create HTML div elements for text labels (positioned absolutely over canvas)
   - Use simple flat shapes (circles, rectangles, lines)
   - Use colors strategically to distinguish elements
   - Add subtle 2D animations (scale pulsing, position floating, opacity changes)

**Return format - use this EXACT structure:**

\`\`\`threejs
<div id="container" style="width: 100%; height: 500px; position: relative;"></div>
<script>
const container = document.getElementById('container');
const scene = new THREE.Scene();
const aspect = container.clientWidth / container.clientHeight;
const camera = new THREE.OrthographicCamera(-5*aspect, 5*aspect, 5, -5, 0.1, 1000);
camera.position.z = 5;
const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
renderer.setSize(container.clientWidth, container.clientHeight);
container.appendChild(renderer.domElement);

// CRITICAL: Store renderer and scene in window for cleanup
window.renderer = renderer;
window.scene = scene;

// Example: Create a labeled 2D circle
const circle = new THREE.Mesh(
    new THREE.CircleGeometry(1, 32),
    new THREE.MeshBasicMaterial({ color: 0x4287f5 })
);
circle.position.set(0, 0, 0);
scene.add(circle);

// Add HTML label for the circle
const label = document.createElement('div');
label.style.position = 'absolute';
label.style.top = '50%';
label.style.left = '50%';
label.style.transform = 'translate(-50%, -50%)';
label.style.color = 'white';
label.style.fontSize = '14px';
label.style.fontWeight = 'bold';
label.style.textShadow = '1px 1px 2px black';
label.textContent = 'Concept Name';
container.appendChild(label);

// Add gentle pulsing animation
function animate() {
    requestAnimationFrame(animate);
    const scale = 1 + Math.sin(Date.now() * 0.001) * 0.1;
    circle.scale.set(scale, scale, 1);
    renderer.render(scene, camera);
}
animate();
</script>
\`\`\`

**Decision Guide:**
- If content has specific visual components (processes, structures, relationships, flows) → CREATE VISUAL
- If content is purely abstract/philosophical with no visual elements → Return NO_VISUAL_NEEDED
- When in doubt, create a simple diagram with labels demonstrating the key concepts

**IMPORTANT:**
- If the content has NO visual components to demonstrate, return EXACTLY: NO_VISUAL_NEEDED
- Otherwise, return ONLY the threejs code block (nothing else)
- The visual MUST be specific to the section content, not generic

**SECTION CONTENT TO VISUALIZE:**
${sectionContent}`;

        const response = await invokeBedrock(visualPrompt, false, 'qwen3-coder'); // Use Qwen3 Coder for visual generation

        // Check if visual is needed
        if (!response?.content || response.content.toUpperCase().includes('NO_VISUAL_NEEDED')) {
            return null;
        }

        // Extract threejs code block if it exists
        let visualCode: string | null = null;
        if (typeof response.content === 'string' && response.content.includes('```threejs')) {
            visualCode = response.content.trim();
        }

        // Save to database (ONE TIME) if we have valid visual code
        if (visualCode) {
            const { error: updateError } = await supabase
                .from('sections')
                .update({ visual_code: visualCode })
                .eq('id', sectionId);

            if (updateError) {
                // Don't throw - return the visual even if save failed
            } else {
            }

            return visualCode;
        }

        return null;
    } catch (error) {
        // Don't throw - visual generation failure shouldn't break the page
        return null;
    }
}

/**
 * Generates a test for a specific section
 */
export async function generateSectionTest(
    sectionId: string,
    courseName: string,
    chapterName: string,
    sectionName: string,
    sectionContent: string
): Promise<{ testId: string; questions: any[] }> {
    try {
        

        // Check if test already exists
        const { data: existingTests, error: testError } = await supabase
            .from('tests')
            .select('id')
            .eq('section_id', sectionId)
            .limit(1); // Get just the first test if multiple exist

        let existingTest: any = null; // Define outside if block

        // If we got a test
        if (existingTests && existingTests.length > 0 && !testError) {
            existingTest = existingTests[0];
            // Check if test has questions
            const { data: existingQuestions } = await supabase
                .from('questions')
                .select('*')
                .eq('test_id', existingTest.id);

            if (existingQuestions && existingQuestions.length > 0) {
                
                return { testId: existingTest.id, questions: existingQuestions };
            }
        } else if (testError) {
            
        }

        // Generate test questions (5-7 questions as per spec)
        // Use prompt sanitization to prevent injection attacks
        const testPrompt = new SafePromptBuilder()
            .withSystemMessage(createDefensiveSystemMessage())
            .addInstruction(`You are creating a test for a course. The course details and content are provided in the metadata and content sections below.

Generate 5-7 multiple choice questions to test understanding of this section. Each question should have 4 options.

Use this EXACT format:
Q1: [Question text]
A) [Option 1]
B) [Option 2]
C) [Option 3]
D) [Option 4]
ANSWER: [A/B/C/D]

Q2: [Question text]
A) [Option 1]
B) [Option 2]
C) [Option 3]
D) [Option 4]
ANSWER: [A/B/C/D]

Continue for all 5-7 questions.`)
            .addMetadata({
                course_name: courseName,
                chapter_name: chapterName,
                section_name: sectionName
            })
            .addUserInput(sectionContent, 'section_content')
            .build();

        const testResponse = await invokeBedrock(testPrompt, false);

        // Parse the test response
        const questions = parseTestQuestions(testResponse.content);

        // Create test entry
        let testId: string;
        if (existingTest) {
            testId = existingTest.id;
        } else {
            const { data: testData, error: testError } = await supabase
                .from('tests')
                .insert({ section_id: sectionId })
                .select()
                .single();

            if (testError || !testData) {
                throw new Error(`Failed to create test: ${testError?.message}`);
            }
            testId = testData.id;
        }

        // Save questions
        const questionInserts = questions.map((q, index) => ({
            test_id: testId,
            question: q.question,
            options: q.options,
            correct_answer: q.correctAnswer,
            order: index + 1
        }));

        const { error: questionsError } = await supabase
            .from('questions')
            .insert(questionInserts);

        if (questionsError) {
            throw new Error(`Failed to save questions: ${questionsError.message}`);
        }

        
        return { testId, questions: questionInserts };
    } catch (error) {
        
        // Pass through friendly errors, translate others
        if (error instanceof Error && error.name && error.name.includes('ERROR')) {
            throw error;
        }
        throw getUserFriendlyError(error);
    }
}

/**
 * Parses test questions from AI response
 */
function parseTestQuestions(rawText: string): Array<{
    question: string;
    options: string[];
    correctAnswer: number;
}> {
    const questions: Array<{
        question: string;
        options: string[];
        correctAnswer: number;
    }> = [];

    const questionBlocks = rawText.split(/Q\d+:/i).filter(block => block.trim());

    for (const block of questionBlocks) {
        const lines = block.split('\n').filter(line => line.trim());
        
        if (lines.length < 5) continue; // Need question + 4 options + answer

        const question = lines[0].trim();
        const options: string[] = [];
        let correctAnswer = 0;

        for (const line of lines.slice(1)) {
            const optionMatch = line.match(/^([A-D])\)\s*(.+)$/i);
            if (optionMatch) {
                options.push(optionMatch[2].trim());
            }

            const answerMatch = line.match(/^ANSWER:\s*([A-D])/i);
            if (answerMatch) {
                const answerLetter = answerMatch[1].toUpperCase();
                correctAnswer = answerLetter.charCodeAt(0) - 'A'.charCodeAt(0);
            }
        }

        if (question && options.length === 4) {
            questions.push({ question, options, correctAnswer });
        }
    }

    return questions;
}

/**
 * Generates a hands-on project for a chapter
 * Called when user completes all section quizzes in a chapter
 */
export async function generateChapterProject(
    chapterId: string,
    courseName: string,
    chapterName: string,
    sections: Section[]
): Promise<{ projectId: string; instructions: string }> {
    try {
        

        // Check if project already exists
        const { data: existingProject } = await supabase
            .from('chapter_projects')
            .select('*')
            .eq('chapter_id', chapterId)
            .single();

        if (existingProject) {
            
            return {
                projectId: existingProject.id,
                instructions: existingProject.instructions
            };
        }

        // Aggregate chapter content for context (first 500 chars of each section)
        const sectionSummaries = sections
            .map((section, index) => {
                const contentPreview = section.content?.substring(0, 500) || '[Content not yet generated]';
                return `Section ${index + 1}: ${section.name}\n${contentPreview}...`;
            })
            .join('\n\n');

        // Generate project instructions
        // Use prompt sanitization to prevent injection attacks

        // Determine if this is a coding/programming subject
        const isCodingSubject = /programming|coding|software|web development|app development|python|javascript|java|c\+\+|react|angular|vue|frontend|backend|full stack|algorithm|data structures/i.test(courseName);

        const projectType = isCodingSubject ? 'hands-on coding project' : 'hands-on practice project';
        const projectInstruction = isCodingSubject
            ? 'Create a practical, real-world coding project'
            : 'Create a practical, hands-on project (NO CODING - use pen/paper, calculator, or conceptual exercises)';

        const criticalInstructions = isCodingSubject
            ? '- This IS a coding/programming course - create a coding project with code examples'
            : `- This is NOT a coding course - DO NOT require any programming/coding
- Use pen and paper, calculator, spreadsheet, or mental exercises
- For STEM subjects: provide practice problems, calculations, derivations, or experiments
- For non-STEM subjects: provide research tasks, analysis exercises, or creative projects`;

        const projectPrompt = new SafePromptBuilder()
            .withSystemMessage(createDefensiveSystemMessage())
            .addInstruction(`You are creating a ${projectType} for a course. The course details and section summaries are provided in the metadata below.

${projectInstruction} that:
- Applies concepts from all sections in this chapter
- Can be completed in 30-60 minutes
- Includes clear, step-by-step instructions
- Is appropriate for learners who just completed this chapter
- Provides expected outcomes/results to verify completion
- Includes 2-3 optional bonus challenges for advanced learners

**CRITICAL INSTRUCTIONS BASED ON SUBJECT TYPE:**
${criticalInstructions}

Format your response as markdown with these sections:
# 📁 Project Title (make it engaging and relevant)

## Objective
[1-2 sentences describing what the learner will ${isCodingSubject ? 'build/create' : 'work on/complete'}]

## Prerequisites
[List any tools, setup, or prior knowledge needed - ${isCodingSubject ? 'code editor, libraries, etc.' : 'pen/paper, calculator, or other non-coding materials'}]

## Instructions
[Numbered steps with clear, actionable instructions. Be specific but not overly prescriptive - learners should have some creative freedom]

## Expected Results
[Describe what success looks like - what should the project ${isCodingSubject ? 'produce/demonstrate' : 'show/achieve'} when complete?]

## Bonus Challenges
[2-3 optional extensions to deepen learning]

## Reflection Questions
[2-3 questions to help learners reflect on what they learned]

IMPORTANT: Make this practical and achievable. The project should reinforce learning, not overwhelm.`)
            .addMetadata({
                course_name: courseName,
                chapter_name: chapterName
            })
            .addUserInput(sectionSummaries, 'section_summaries')
            .build();

        const projectResponse = await invokeBedrock(projectPrompt, false);

        // Save project to database
        const { data: projectData, error: projectError } = await supabase
            .from('chapter_projects')
            .insert({
                chapter_id: chapterId,
                instructions: projectResponse.content
            })
            .select()
            .single();

        if (projectError || !projectData) {
            throw new Error(`Failed to create chapter project: ${projectError?.message}`);
        }

        
        return {
            projectId: projectData.id,
            instructions: projectData.instructions
        };
    } catch (error) {
        
        throw error;
    }
}

/**
 * Checks if all section quizzes in a chapter are completed
 * If yes, unlocks the chapter project and generates it if needed
 */
export async function checkAndUnlockChapterProject(
    userId: string,
    courseId: string,
    chapterId: string
): Promise<{ projectUnlocked: boolean; allQuizzesComplete: boolean }> {
    try {
        

        // Get all sections in this chapter
        const { data: sections, error: sectionsError } = await supabase
            .from('sections')
            .select('id')
            .eq('chapter_id', chapterId)
            .order('order');

        if (sectionsError || !sections || sections.length === 0) {
            
            return { projectUnlocked: false, allQuizzesComplete: false };
        }

        const sectionIds = sections.map(s => s.id);

        // Check progress for all sections
        const { data: progressRecords, error: progressError } = await supabase
            .from('user_progress')
            .select('section_id, quiz_completed, quiz_score')
            .eq('user_id', userId)
            .in('section_id', sectionIds);

        if (progressError) {
            
            return { projectUnlocked: false, allQuizzesComplete: false };
        }

        // Check if all sections have completed quizzes with passing score
        const allQuizzesComplete = sectionIds.every(sectionId => {
            const progress = progressRecords?.find(p => p.section_id === sectionId);
            return progress?.quiz_completed && (progress?.quiz_score ?? 0) >= 60;
        });

        

        if (!allQuizzesComplete) {
            return { projectUnlocked: false, allQuizzesComplete: false };
        }

        // All quizzes complete! Update or create chapter progress
        const now = new Date().toISOString();

        const { data: existingProgress } = await supabase
            .from('user_chapter_progress')
            .select('*')
            .eq('user_id', userId)
            .eq('chapter_id', chapterId)
            .single();

        if (existingProgress) {
            // Update existing record
            await supabase
                .from('user_chapter_progress')
                .update({
                    all_quizzes_completed: true,
                    project_unlocked: true,
                    quizzes_completed_at: now,
                    project_unlocked_at: now
                })
                .eq('id', existingProgress.id);
        } else {
            // Create new record
            await supabase
                .from('user_chapter_progress')
                .insert({
                    user_id: userId,
                    course_id: courseId,
                    chapter_id: chapterId,
                    all_quizzes_completed: true,
                    project_unlocked: true,
                    quizzes_completed_at: now,
                    project_unlocked_at: now
                });
        }

        // Check if project exists, if not, generate it
        const { data: existingProject } = await supabase
            .from('chapter_projects')
            .select('id')
            .eq('chapter_id', chapterId)
            .single();

        if (!existingProject) {
            // Get chapter and course info for generation
            const { data: chapter } = await supabase
                .from('chapters')
                .select('name, course_id, courses(name)')
                .eq('id', chapterId)
                .single();

            const { data: sectionsWithContent } = await supabase
                .from('sections')
                .select('*')
                .eq('chapter_id', chapterId)
                .order('order');

            if (chapter && sectionsWithContent) {
                // Generate project in background (don't await to avoid blocking)
                generateChapterProject(
                    chapterId,
                    (chapter as any).courses.name,
                    chapter.name,
                    sectionsWithContent
                ).catch(err => {});
            }
        }

        
        return { projectUnlocked: true, allQuizzesComplete: true };
    } catch (error) {
        
        return { projectUnlocked: false, allQuizzesComplete: false };
    }
}

/**
 * Gets the status of a chapter project for a user
 */
export async function getChapterProjectStatus(
    userId: string,
    chapterId: string
): Promise<{
    unlocked: boolean;
    viewed: boolean;
    markedComplete: boolean;
    instructions?: string;
    projectId?: string;
}> {
    try {
        // Get user progress
        const { data: progress } = await supabase
            .from('user_chapter_progress')
            .select('*')
            .eq('user_id', userId)
            .eq('chapter_id', chapterId)
            .single();

        // Get project data
        const { data: project } = await supabase
            .from('chapter_projects')
            .select('*')
            .eq('chapter_id', chapterId)
            .single();

        return {
            unlocked: progress?.project_unlocked || false,
            viewed: progress?.project_viewed || false,
            markedComplete: progress?.project_marked_complete || false,
            instructions: project?.instructions,
            projectId: project?.id
        };
    } catch (error) {
        
        return {
            unlocked: false,
            viewed: false,
            markedComplete: false
        };
    }
}

/**
 * Marks a chapter project as viewed by the user
 */
export async function markChapterProjectViewed(
    userId: string,
    chapterId: string
): Promise<void> {
    try {
        const now = new Date().toISOString();

        await supabase
            .from('user_chapter_progress')
            .update({
                project_viewed: true,
                project_viewed_at: now
            })
            .eq('user_id', userId)
            .eq('chapter_id', chapterId);

        
    } catch (error) {
        
        throw error;
    }
}

/**
 * Marks a chapter project as complete (user self-reports)
 */
export async function markChapterProjectComplete(
    userId: string,
    chapterId: string,
    isComplete: boolean
): Promise<void> {
    try {
        const now = new Date().toISOString();

        await supabase
            .from('user_chapter_progress')
            .update({
                project_marked_complete: isComplete,
                project_completed_at: isComplete ? now : null
            })
            .eq('user_id', userId)
            .eq('chapter_id', chapterId);

        
    } catch (error) {
        
        throw error;
    }
}

/**
 * Fetches a course with all its chapters and sections
 */
export async function getCourseWithContent(courseId: string): Promise<{
    course: Course;
    chapters: (Chapter & { sections: Section[] })[];
}> {
    const { data: course, error: courseError } = await supabase
        .from('courses')
        .select('*')
        .eq('id', courseId)
        .single();

    if (courseError || !course) {
        throw new Error(`Failed to fetch course: ${courseError?.message}`);
    }

    const { data: chapters, error: chaptersError } = await supabase
        .from('chapters')
        .select('*')
        .eq('course_id', courseId)
        .order('order', { ascending: true });

    if (chaptersError) {
        throw new Error(`Failed to fetch chapters: ${chaptersError.message}`);
    }

    const chaptersWithSections = await Promise.all(
        (chapters || []).map(async (chapter) => {
            const { data: sections, error: sectionsError } = await supabase
                .from('sections')
                .select('*')
                .eq('chapter_id', chapter.id)
                .order('order', { ascending: true });

            if (sectionsError) {
                
                return { ...chapter, sections: [] };
            }

            return { ...chapter, sections: sections || [] };
        })
    );

    return { course, chapters: chaptersWithSections };
}

/**
 * Deletes a course and all its associated chapters and sections
 */
export async function deleteCourse(courseId: string): Promise<void> {
    const { error } = await supabase
        .from('courses')
        .delete()
        .eq('id', courseId);

    if (error) {
        throw new Error(`Failed to delete course: ${error.message}`);
    }
}

/**
 * Lists all courses for a user
 * Also checks if course templates still exist and marks courses accordingly
 */
export async function getUserCourses(userId: string): Promise<Course[]> {
    const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

    if (error) {
        throw new Error(`Failed to fetch user courses: ${error.message}`);
    }

    const courses = data || [];

    // Check which courses have deleted templates
    // Get all unique template IDs from courses that claim to be from templates
    const templateIds = courses
        .filter(course => course.is_from_template && course.template_id)
        .map(course => course.template_id)
        .filter((id, index, self) => self.indexOf(id) === index); // unique IDs only

    if (templateIds.length > 0) {
        // Query course_templates to see which templates still exist
        const { data: existingTemplates } = await supabase
            .from('course_templates')
            .select('id')
            .in('id', templateIds);

        const existingTemplateIds = new Set(existingTemplates?.map(t => t.id) || []);

        // Mark courses with deleted templates
        courses.forEach(course => {
            if (course.is_from_template && course.template_id) {
                // Add a runtime property to indicate if template was deleted
                (course as any).template_deleted = !existingTemplateIds.has(course.template_id);
            }
        });
    }

    return courses;
}

/**
 * Initialize user progress for a course - unlock first section
 */
export async function initializeCourseProgress(
    userId: string,
    courseId: string
): Promise<void> {
    try {
        // Get the first section of the first chapter
        const { data: firstSection } = await supabase
            .from('sections')
            .select('id, chapter_id, chapters!inner(course_id, order)')
            .eq('chapters.course_id', courseId)
            .order('chapters.order', { ascending: true })
            .order('order', { ascending: true })
            .limit(1)
            .single();

        if (firstSection) {
            // Create or update progress record to unlock first section
            await supabase
                .from('user_progress')
                .upsert({
                    user_id: userId,
                    course_id: courseId,
                    section_id: firstSection.id,
                    unlocked: true,
                    quiz_completed: false
                }, {
                    onConflict: 'user_id,section_id'
                });

            
        }
    } catch (error) {
        
        throw error;
    }
}

/**
 * Get user's progress for a course
 */
export async function getUserCourseProgress(
    userId: string,
    courseId: string
): Promise<any[]> {
    try {
        const { data, error } = await supabase
            .from('user_progress')
            .select('*')
            .eq('user_id', userId)
            .eq('course_id', courseId);

        if (error) throw error;
        return data || [];
    } catch (error) {
        
        throw error;
    }
}

/**
 * Check if a section is unlocked for a user
 */
export async function isSectionUnlocked(
    userId: string,
    sectionId: string
): Promise<boolean> {
    try {
        const { data } = await supabase
            .from('user_progress')
            .select('unlocked')
            .eq('user_id', userId)
            .eq('section_id', sectionId)
            .single();

        return data?.unlocked || false;
    } catch (error) {
        
        return false;
    }
}

/**
 * Submit quiz answers and calculate score
 */
export async function submitQuiz(
    userId: string,
    courseId: string,
    sectionId: string,
    answers: { questionId: string; selectedAnswer: number }[]
): Promise<{
    score: number;
    passed: boolean;
    totalQuestions: number;
    incorrectQuestions?: Array<{
        question: string;
        options: string[];
        userAnswer: number;
    }>;
}> {
    try {
        // Get test ID
        const { data: tests, error: testError } = await supabase
            .from('tests')
            .select('id')
            .eq('section_id', sectionId)
            .limit(1); // Get just the first test if multiple exist

        if (testError) {
            
            throw new Error(`Error fetching test: ${testError.message}`);
        }

        if (!tests || tests.length === 0) {
            throw new Error('Test not found for this section');
        }

        const test = tests[0];

        // Get questions for this test
        const { data: questions, error: questionsError } = await supabase
            .from('questions')
            .select('*')
            .eq('test_id', test.id);

        if (questionsError) {
            
            throw new Error(`Questions not found: ${questionsError.message}`);
        }

        if (!questions || questions.length === 0) {
            throw new Error('No questions found for test');
        }

        // Calculate score and track incorrect questions
        let correctCount = 0;
        const totalQuestions = questions.length;
        const incorrectQuestions: Array<{
            question: string;
            options: string[];
            userAnswer: number;
        }> = [];

        questions.forEach((question: any) => {
            const userAnswer = answers.find(a => a.questionId === question.id);
            if (userAnswer && userAnswer.selectedAnswer === question.correct_answer) {
                correctCount++;
            } else if (userAnswer) {
                // User got it wrong - record details (but NOT the correct answer)
                incorrectQuestions.push({
                    question: question.question,
                    options: question.options,
                    userAnswer: userAnswer.selectedAnswer
                });
            }
        });

        const score = (correctCount / totalQuestions) * 100;
        const passed = score >= 60; // 60% passing threshold

        // Update user progress
        await supabase
            .from('user_progress')
            .update({
                quiz_completed: true,
                quiz_score: score,
                completed_at: new Date().toISOString()
            })
            .eq('user_id', userId)
            .eq('course_id', courseId)
            .eq('section_id', sectionId);

        // If passed, unlock next section
        if (passed) {
            await unlockNextSection(userId, courseId, sectionId);

            // Check if all chapter quizzes are complete and unlock chapter project
            const { data: section } = await supabase
                .from('sections')
                .select('chapter_id')
                .eq('id', sectionId)
                .single();

            if (section?.chapter_id) {
                // Run in background to avoid blocking quiz submission
                checkAndUnlockChapterProject(userId, courseId, section.chapter_id)
                    .catch(err => {});
            }
        }

        return {
            score,
            passed,
            totalQuestions,
            incorrectQuestions: incorrectQuestions.length > 0 ? incorrectQuestions : undefined
        };
    } catch (error) {
        
        throw error;
    }
}

/**
 * Submit user feedback for a section (thumbs up/down after passing quiz)
 */
export async function submitSectionFeedback(
    userId: string,
    sectionId: string,
    rating: 'positive' | 'negative'
): Promise<void> {
    try {
        const { error } = await supabase
            .from('user_progress')
            .update({
                feedback_rating: rating,
                feedback_submitted_at: new Date().toISOString()
            })
            .eq('user_id', userId)
            .eq('section_id', sectionId);

        if (error) {
            throw new Error(`Failed to submit feedback: ${error.message}`);
        }

    } catch (error) {
        throw error;
    }
}

/**
 * Get feedback status for a section
 */
export async function getSectionFeedback(
    userId: string,
    sectionId: string
): Promise<{ rating: 'positive' | 'negative' | null; submittedAt: string | null }> {
    try {
        const { data, error } = await supabase
            .from('user_progress')
            .select('feedback_rating, feedback_submitted_at')
            .eq('user_id', userId)
            .eq('section_id', sectionId)
            .single();

        if (error) {
            return { rating: null, submittedAt: null };
        }

        return {
            rating: data?.feedback_rating || null,
            submittedAt: data?.feedback_submitted_at || null
        };
    } catch (error) {
        return { rating: null, submittedAt: null };
    }
}

/**
 * Check if a course template is fully completed and mark it
 * Also syncs any incomplete courses to this template when it's marked complete
 */
async function checkAndMarkTemplateComplete(templateId: string): Promise<void> {
    try {
        // Get all sections for this template
        const { data: templateData } = await supabase
            .from('course_templates')
            .select('template_data, is_completed')
            .eq('id', templateId)
            .single();

        if (!templateData?.template_data?.chapters) {
            return;
        }

        // Don't re-process if already marked complete
        if (templateData.is_completed) {
            return;
        }

        const chapters = templateData.template_data.chapters;

        // Check if all sections have content
        for (const chapter of chapters) {
            if (!chapter.sections || !Array.isArray(chapter.sections)) {
                return;
            }

            for (const section of chapter.sections) {
                if (!section.content || section.content.trim() === '') {

                    return; // Not complete
                }
            }
        }

        // All sections have content - use the new atomic function to mark complete and sync

        const { data: result, error } = await supabase.rpc('complete_template_and_sync', {
            p_template_id: templateId
        });

        if (error) {
            // Fallback to manual update if RPC doesn't exist
            await supabase
                .from('course_templates')
                .update({ is_completed: true })
                .eq('id', templateId);
        } else if (result && result.length > 0) {
        }


    } catch (error) {
    }
}

/**
 * Unlock the next section after passing a quiz
 */
async function unlockNextSection(
    userId: string,
    courseId: string,
    currentSectionId: string
): Promise<void> {
    try {
        // Get current section info
        const { data: currentSection } = await supabase
            .from('sections')
            .select('id, order, chapter_id, chapters!inner(order, course_id)')
            .eq('id', currentSectionId)
            .single();

        if (!currentSection) return;

        // Try to get next section in same chapter
        let { data: nextSection } = await supabase
            .from('sections')
            .select('id')
            .eq('chapter_id', currentSection.chapter_id)
            .gt('order', currentSection.order)
            .order('order', { ascending: true })
            .limit(1)
            .single();

        // If no next section in chapter, get first section of next chapter
        if (!nextSection) {
            const { data: nextChapterSection } = await supabase
                .from('sections')
                .select('id, chapter_id, chapters!inner(order, course_id)')
                .eq('chapters.course_id', courseId)
                .gt('chapters.order', currentSection.chapters.order)
                .order('chapters.order', { ascending: true })
                .order('order', { ascending: true })
                .limit(1)
                .single();

            nextSection = nextChapterSection;
        }

        if (nextSection) {
            // Unlock next section
            await supabase
                .from('user_progress')
                .upsert({
                    user_id: userId,
                    course_id: courseId,
                    section_id: nextSection.id,
                    unlocked: true,
                    quiz_completed: false
                }, {
                    onConflict: 'user_id,course_id,section_id'
                });


        } else {
            // No more sections - course is complete!

            // Get course data including name and template_id
            const { data: courseData } = await supabase
                .from('courses')
                .select('id, name, template_id')
                .eq('id', courseId)
                .single();

            if (!courseData) return;

            // Mark the user's course as completed
            await supabase
                .from('courses')
                .update({ is_completed: true })
                .eq('id', courseId);

            // Premium courses do NOT create or update templates
            // Only courses created from course_templates should have template_id
            // This is intentionally left empty - no template logic for premium-generated courses
        }
    } catch (error) {
        
        throw error;
    }
}

/**
 * Creates a course from a template for a user (by template ID)
 * This reads the template data and creates all necessary database records
 */
export async function createCourseFromTemplateById(
    templateId: string,
    userId: string
): Promise<Course> {
    

    try {
        // 1. Fetch the template
        const { data: template, error: templateError } = await supabase
            .from('course_templates')
            .select('*')
            .eq('id', templateId)
            .single();

        if (templateError) {
            throw new Error(`Failed to fetch template: ${templateError.message}`);
        }

        if (!template) {
            throw new Error('Template not found');
        }

        // 2. Create the course record
        const { data: course, error: courseError } = await supabase
            .from('courses')
            .insert({
                user_id: userId,
                name: template.original_name,
                template_id: template.id,
                is_from_template: true,
                is_completed: false
            })
            .select()
            .single();

        if (courseError) {
            throw new Error(`Failed to create course: ${courseError.message}`);
        }

        

        // 3. Create chapters and sections from template data
        const templateData = template.template_data as GeneratedCourse;
        const createdSections: string[] = []; // Track section IDs
        const chapterProjects = (templateData as any).chapter_projects || {};

        if (templateData.chapters && Array.isArray(templateData.chapters)) {
            for (let chapterIndex = 0; chapterIndex < templateData.chapters.length; chapterIndex++) {
                const chapterData = templateData.chapters[chapterIndex];

                // Create chapter
                const { data: chapter, error: chapterError } = await supabase
                    .from('chapters')
                    .insert({
                        course_id: course.id,
                        name: chapterData.name,
                        order: chapterIndex + 1
                    })
                    .select()
                    .single();

                if (chapterError) {

                    continue;
                }

                // Create chapter project if it exists in template
                if (chapterProjects[chapterIndex]) {
                    await supabase
                        .from('chapter_projects')
                        .insert({
                            chapter_id: chapter.id,
                            instructions: chapterProjects[chapterIndex]
                        });

                }

                // Create sections for this chapter
                if (chapterData.sections && Array.isArray(chapterData.sections)) {
                    for (let sectionIndex = 0; sectionIndex < chapterData.sections.length; sectionIndex++) {
                        const sectionData = chapterData.sections[sectionIndex];

                        const { data: section, error: sectionError } = await supabase
                            .from('sections')
                            .insert({
                                chapter_id: chapter.id,
                                name: sectionData.name,
                                content: sectionData.content || '',
                                
                                order: sectionIndex + 1
                            })
                            .select()
                            .single();

                        if (sectionError) {
                            
                        } else if (section) {
                            createdSections.push(section.id);
                        }
                    }
                }
            }
        }

        // 4. Initialize user_progress for all sections
        // Only unlock the first section for non-premium users
        if (createdSections.length > 0) {
            const progressEntries = createdSections.map((sectionId, index) => ({
                user_id: userId,
                course_id: course.id,
                section_id: sectionId,
                unlocked: index === 0, // Only first section is unlocked
                quiz_completed: false,
                quiz_score: null
            }));

            const { error: progressError } = await supabase
                .from('user_progress')
                .insert(progressEntries);

            if (progressError) {
                
            }
        }

        // 5. Increment template usage count
        const { error: updateError } = await supabase
            .from('course_templates')
            .update({ 
                usage_count: (template.usage_count || 0) + 1 
            })
            .eq('id', template.id);

        if (updateError) {
            
        }


        return course;

    } catch (err) {

        throw err;
    }
}

/**
 * Fetch course templates with filtering, sorting, and pagination
 * For the CourseGallery page
 */
export async function getCourseTemplates(options: {
    searchQuery?: string;
    category?: string;
    sortBy?: 'popularity' | 'newest' | 'alphabetical';
    limit?: number;
    offset?: number;
}): Promise<{ templates: CourseTemplate[]; total: number }> {
    try {
        const { searchQuery, category, sortBy = 'popularity', limit = 20, offset = 0 } = options;

        // Start building query
        let query = supabase
            .from('course_templates')
            .select('*', { count: 'exact' })
            .eq('is_completed', true);

        // Apply search filter
        if (searchQuery && searchQuery.trim()) {
            query = query.ilike('original_name', `%${searchQuery.trim()}%`);
        }

        // Apply category filter
        if (category && category !== 'All') {
            query = query.eq('category', category);
        }

        // Apply sorting
        switch (sortBy) {
            case 'popularity':
                query = query.order('usage_count', { ascending: false, nullsFirst: false });
                break;
            case 'newest':
                query = query.order('created_at', { ascending: false });
                break;
            case 'alphabetical':
                query = query.order('original_name', { ascending: true });
                break;
        }

        // Apply pagination
        query = query.range(offset, offset + limit - 1);

        const { data, error, count } = await query;

        if (error) {
            throw new Error(`Failed to fetch courses: ${error.message}`);
        }

        return {
            templates: data || [],
            total: count || 0
        };
    } catch (error) {
        throw error;
    }
}

/**
 * Get featured courses for the landing page (8 courses rotated weekly)
 * Uses client-side rotation logic based on week number
 */
export async function getFeaturedCourses(): Promise<CourseTemplate[]> {
    try {
        // Fetch top courses by popularity (top 30 to allow for rotation)
        const { data, error } = await supabase
            .from('course_templates')
            .select('*')
            .eq('is_completed', true)
            .order('usage_count', { ascending: false, nullsFirst: false })
            .limit(30);

        if (error) {
            throw new Error(`Failed to fetch featured courses: ${error.message}`);
        }

        return data || [];
    } catch (error) {
        throw error;
    }
}